import {
  CustomerAddressService,
  CustomerContactService,
  CustomerService,
} from "@common/client"
import { makeAxiosMutation, makeQuery } from "./utils"

// Customers list
export const customersKey = ["customers"] as const

export const customersQuery = makeQuery({
  serviceCall: CustomerService.listCustomers,
  queryKey: (params) => [...customersKey, params ?? {}],
})

// Customer detail
export const customerKey = (id: string) => ["customer", id] as const

export const customerQuery = makeQuery({
  serviceCall: (params: Parameters<typeof CustomerService.getCustomer>[0]) =>
    CustomerService.getCustomer({ ...params, include_archived: true }),
  queryKey: (params) => customerKey(params.customer_id),
})

// Mutation hooks
export const useCreateCustomerMutation = makeAxiosMutation({
  serviceCall: CustomerService.createCustomer,
  invalidates: () => [customersKey],
})

export const useUpdateCustomerMutation = makeAxiosMutation({
  serviceCall: CustomerService.updateCustomer,
  invalidates: (params) => [customersKey, customerKey(params.customer_id)],
})

export const useArchiveCustomerMutation = makeAxiosMutation({
  serviceCall: CustomerService.archiveCustomer,
  invalidates: (params) => [customersKey, customerKey(params.customer_id)],
})

export const customerContactsKey = (customerId: string | null) =>
  ["customer-contacts", customerId] as const

export const customerContactsQuery = makeQuery({
  serviceCall: CustomerContactService.listContacts,
  queryKey: (params) => [
    ...customerContactsKey(params?.customer_id ?? null),
    params ?? {},
  ],
})

export const customerContactBaseKey = ["customer-contact"] as const

export const customerContactKey = (id: string) =>
  [...customerContactBaseKey, id] as const

export const customerContactQuery = makeQuery({
  serviceCall: (params: Parameters<typeof CustomerContactService.get>[0]) =>
    CustomerContactService.get({ ...params, include_archived: true }),
  queryKey: (params) => customerContactKey(params.contact_id),
})

export const useCreateCustomerContactMutation = makeAxiosMutation({
  serviceCall: CustomerContactService.create,
  invalidates: (params) => [
    customerContactsKey(params.customerContactCreate.customer_id),
  ],
})

export const useUpdateCustomerContactMutation = makeAxiosMutation({
  serviceCall: CustomerContactService.update,
  invalidates: (_params, contact) => [
    customerContactsKey(contact.customer_id),
    customerContactKey(contact.id),
  ],
})

export const useArchiveCustomerContactMutation = makeAxiosMutation({
  serviceCall: CustomerContactService.archive,
  invalidates: (_params, contact) => [
    customerContactsKey(contact.customer_id),
    customerContactKey(contact.id),
  ],
})

export const customerAddressesKey = (customerId: string | null) =>
  ["customer-addresses", customerId] as const

export const customerAddressesQuery = makeQuery({
  serviceCall: CustomerAddressService.listAddresses,
  queryKey: (params) => customerAddressesKey(params?.customer_id ?? null),
})

export const useRestoreCustomerMutation = makeAxiosMutation({
  serviceCall: CustomerService.restore,
  invalidates: (params) => [customersKey, customerKey(params.customer_id)],
})

export const useDeleteCustomerMutation = makeAxiosMutation({
  serviceCall: CustomerService.deleteCustomer,
  invalidates: (params) => [customersKey, customerKey(params.customer_id)],
})

export const useRestoreCustomerContactMutation = makeAxiosMutation({
  serviceCall: CustomerContactService.restore,
  invalidates: (_params, contact) => [
    customerContactsKey(contact.customer_id),
    customerContactKey(contact.id),
  ],
})
